import React, { useEffect, useState } from 'react';
import {
	DestinationSummaryForDestinationMultiple,
	HotelOptionDetailDestinationMultiple,
	QuotationDestinationMultipleSummaryPriceCombination,
	QuotationServiceSimple,
} from '../../types';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Card,
	Container,
	Grid,
	IconButton,
	Paper,
	Drawer,
	Typography,
} from '@mui/material';
import { HotelDetailCardMultiDestination } from '../HotelDetailCardMultiDestination';
import { RoomPricesMultiDestinationPreviewList } from '../RoomPricesMultiDestinationPreviewList';
import { AddOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import {
	buildChangeOptionsCopy,
	buildCurrentSelectionLabel,
	buildIncludedCategoriesSummary,
	calculateUtility,
	getBoardName,
	getCityNameCleaned,
	GetPAXTotalsResponse,
	hasErrorInDestinationMultipleParams,
	separadorDeMiles,
	hasOnlyOneRoom,
} from '../../helpers';
import { SIGN_GREEN, SIGN_GREEN_BG, SIGN_GREEN_BORDER, useAgencyBranding } from '../../branding';
import { CardsCarousel } from '../CardsCarousel';
import { PaymentTeaser } from '../PaymentTeaser';
import useBreakpoints from '../../../common/hooks/useBreakpoints';
import { IncludeMultiDestination } from '../IncludeMultiDestination';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { countryWAPMapping } from 'constants/constants';
import PaymentsIcon from '@mui/icons-material/Payments';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import { FlightOption } from './FlightOption';
import CloseIcon from '@mui/icons-material/Close';
import { ReserveCta } from '../SelectionSummary/ReserveCta';
import { HotelOptionItem } from './HotelOptionItem';

const EXTERNAL_ACCENT_COLOR = '#06036c';
const DEFAULT_ORANGE_BG = '#ED6602';
const DEFAULT_PURPLE_BG = '#31194B';
const SECTION_SHADOW = '0 8px 20px rgba(49, 25, 75, 0.16)';

/** Alto real de la barra fija en mobile (medido): reserva de espacio al final. */
export const MULTI_SUMMARY_BAR_HEIGHT = 172;

interface Props {
	destinations: DestinationSummaryForDestinationMultiple[];
	priceCombinations: QuotationDestinationMultipleSummaryPriceCombination[];
	paxTotals: GetPAXTotalsResponse;
	manualServices: QuotationServiceSimple[];
	showUtility: boolean;
	markup: number | null;
	totalFlights: number;
	flightIndexSelected: number | null;
	handleFlightSelectionChange?: (index: number | null) => void;
	exchangeRate: number;
	localCurrency: string;
	showLocalCurrency?: boolean;
	quotationId?: number;
	isExternal?: boolean;
	selectedOptionalsTotalUSD?: number;
	selectedAssistanceTotalUSD?: number;
	selectedAssistanceName?: string;
}

export interface SelectedHotelByDestination {
	destinationIndex: number;
	destinationCityCode: string;
	hotel: HotelOptionDetailDestinationMultiple;
}

export const MultiSelectHotelsMultiDestination = ({
	destinations,
	priceCombinations,
	paxTotals,
	manualServices,
	showUtility,
	markup,
	totalFlights,
	flightIndexSelected,
	handleFlightSelectionChange,
	exchangeRate,
	localCurrency,
	showLocalCurrency,
	quotationId,
	isExternal,
	selectedOptionalsTotalUSD = 0,
	selectedAssistanceTotalUSD = 0,
	selectedAssistanceName,
}: Props): JSX.Element => {
	const branding = useAgencyBranding(isExternal);
	const sectionHeaderSx = {
		marginBottom: '10px',
		marginTop: '14px',
		width: '100%',
		background: isExternal
			? EXTERNAL_ACCENT_COLOR
			: `linear-gradient(135deg, ${DEFAULT_ORANGE_BG} 0%, #f07c2b 42%, ${DEFAULT_PURPLE_BG} 145%)`,
		paddingLeft: '16px',
		paddingRight: '16px',
		paddingTop: '9px',
		paddingBottom: '9px',
		borderRadius: '10px',
		fontSize: '1rem',
		fontWeight: 700,
		color: 'white',
		boxShadow: SECTION_SHADOW,
	};
	const drawerSectionTitleSx = {
		marginTop: '14px',
		marginBottom: '8px',
		paddingLeft: '4px',
		fontSize: '0.95rem',
		fontWeight: 800,
		color: 'primary.main',
	};
	const drawerScrollSx = {
		overflowX: 'auto',
		overflowY: 'hidden',
		scrollbarWidth: 'none',
		'&::-webkit-scrollbar': {
			display: 'none',
		},
	};
	// OJO: este guard retornaba ANTES de la mayoría de los hooks. Mientras el
	// agente arma la cotización los datos pasan por estados inválidos (destino
	// nuevo sin hoteles => keys != destinations) y el flag alterna: con el
	// return temprano, la cantidad de hooks cambiaba entre renders y React
	// tiraba "Rendered fewer/more hooks" => pantalla en blanco al crear multis.
	// Ahora es un flag y el return está DESPUÉS del último hook.
	const hasInvalidParams = hasErrorInDestinationMultipleParams(priceCombinations, destinations, paxTotals);
	const isMediumDown = useBreakpoints().mediumDown;
	// Alto que la barra fija le tapa al contenido: se reserva al final de la
	// página para que nada quede escondido detrás.
	const bottomBarSpace = isMediumDown ? MULTI_SUMMARY_BAR_HEIGHT : 96;

	/**En  priceCombinations se envian todas las combinaciones posibles cuando hay mas de 1 vuelo filtramos las que nos hace falta para mostrar**/

	const validCombinations =
		totalFlights > 1
			? priceCombinations.filter((x) => x.keys[0].flightIndex == flightIndexSelected)
			: priceCombinations;
	const priceCombinationsSorted = validCombinations.sort(function (a, b) {
		return a.summaryPriceForOneAdult - b.summaryPriceForOneAdult;
	});

	const combinationMinPrice = priceCombinationsSorted[0];
	const [selectedCombination, setSelectedCombination] =
		useState<QuotationDestinationMultipleSummaryPriceCombination | null>(combinationMinPrice ?? null);
	const [openDrawer, setOpenDrawer] = useState<boolean>(false);
	// Accordion controlado: arranca SIEMPRE cerrado (decidido: no desplegarlo
	// por default, empuja el total y el CTA abajo del fold) y se resetea al
	// cerrar el drawer, para que la próxima apertura no aparezca abierto.
	const [openOptions, setOpenOptions] = useState<boolean>(false);
	const [showAlternativeCurrency, setShowAlternativeCurrency] = useState<boolean>(false);
	/**Permite que al cargar por primera vez busque la mejor opción y no la que se le pase por default**/
	useEffect(() => {
		if (totalFlights > 1) {
			const allPriceCombinationsSorted = priceCombinations.sort(function (a, b) {
				return a.summaryPriceForOneAdult - b.summaryPriceForOneAdult;
			});
			const maybeFlightIndex = allPriceCombinationsSorted[0]?.keys[0]?.flightIndex;

			if (maybeFlightIndex != null && handleFlightSelectionChange) {
				handleFlightSelectionChange(maybeFlightIndex);
			}
		}
	}, []);

	const getSelectedHotels = (): SelectedHotelByDestination[] => {
		// Con datos a medio armar puede no haber combinación, o las keys pueden
		// apuntar a un destino/hotel que ya no existe: se saltea en vez de romper.
		if (!combinationMinPrice) return [];
		return combinationMinPrice.keys
			.map((key) => {
				const destination =
					destinations[
						destinations.findIndex((x, index) => x.cityCode == key.destinationCode && key.destinationIndex == index)
					];
				const hotel =
					destination?.hotelOptions[
						destination.hotelOptions.findIndex(
							(x) => x.hotelId == key.hotelId && x.provider == key.hotelProvider && key.boardsKey == x.boardsKey,
						)
					];
				if (!destination || !hotel) return null;
				return {
					destinationIndex: key.destinationIndex,
					destinationCityCode: key.destinationCode,
					hotel: hotel,
				};
			})
			.filter(Boolean) as SelectedHotelByDestination[];
	};

	useEffect(() => {
		setSelectedCombination(combinationMinPrice);
		setSelectedHotels(getSelectedHotels());
	}, [priceCombinations, flightIndexSelected]);

	const [selectedHotels, setSelectedHotels] = useState<SelectedHotelByDestination[]>(getSelectedHotels());

	const getDestinationIndex = (destinationCityCode: string, destinationIndex: number) =>
		selectedHotels.findIndex(
			(hotel) => hotel.destinationCityCode == destinationCityCode && hotel.destinationIndex == destinationIndex,
		);

	const handleSelectHotel = (
		destinationCityCode: string,
		destinationIndex: number,
		hotel: HotelOptionDetailDestinationMultiple,
	) => {
		const maybeDestinationSelected = getDestinationIndex(destinationCityCode, destinationIndex);
		const selectedHotelsCopy = [...selectedHotels];
		if (
			maybeDestinationSelected > -1 &&
			!isSelectedHotel(hotel.hotelId, destinationCityCode, destinationIndex, hotel.provider, hotel.boardsKey)
		) {
			selectedHotelsCopy.splice(maybeDestinationSelected, 1);
			const selectedNew = [
				...selectedHotelsCopy,
				{
					destinationCityCode,
					destinationIndex,
					hotel: hotel,
				},
			];

			const possiblesCombinations = validCombinations.filter(
				(x) =>
					x.keys.findIndex(
						(k) =>
							k.destinationCode == destinationCityCode &&
							k.destinationIndex == destinationIndex &&
							k.hotelId == hotel.hotelId &&
							k.hotelProvider == hotel.provider,
					) > -1,
			);
			const combinationForHotelsSelection = possiblesCombinations.find((x) =>
				x.keys.every(
					(k) =>
						selectedNew.findIndex(
							(s) =>
								s.destinationCityCode == k.destinationCode &&
								s.hotel.hotelId == k.hotelId &&
								s.hotel.provider == k.hotelProvider &&
								s.hotel.boardsKey === k.boardsKey,
						) > -1,
				),
			);
			if (!combinationForHotelsSelection) return;
			setSelectedCombination(combinationForHotelsSelection);
			setSelectedHotels(selectedNew);
		}
	};

	const findCombinationForHotelSelection = (
		destinationCityCode: string,
		destinationIndex: number,
		hotel: HotelOptionDetailDestinationMultiple,
	) => {
		const maybeDestinationSelected = getDestinationIndex(destinationCityCode, destinationIndex);
		if (maybeDestinationSelected === -1) return null;
		const selectedHotelsCopy = [...selectedHotels];
		selectedHotelsCopy.splice(maybeDestinationSelected, 1);
		const selectedNew = [
			...selectedHotelsCopy,
			{
				destinationCityCode,
				destinationIndex,
				hotel,
			},
		];
		return validCombinations.find((combination) =>
			combination.keys.every(
				(key) =>
					selectedNew.findIndex(
						(selectedHotel) =>
							selectedHotel.destinationCityCode == key.destinationCode &&
							selectedHotel.destinationIndex == key.destinationIndex &&
							selectedHotel.hotel.hotelId == key.hotelId &&
							selectedHotel.hotel.provider == key.hotelProvider &&
							selectedHotel.hotel.boardsKey === key.boardsKey,
					) > -1,
			),
		);
	};

	const isHotelAvailableForCurrentSelection = (
		destinationCityCode: string,
		destinationIndex: number,
		hotel: HotelOptionDetailDestinationMultiple,
	) =>
		isSelectedHotel(hotel.hotelId, destinationCityCode, destinationIndex, hotel.provider, hotel.boardsKey) ||
		Boolean(findCombinationForHotelSelection(destinationCityCode, destinationIndex, hotel));

	const isSelectedHotel = (
		hotelId: number,
		destinationCityCode: string,
		destinationIndex: number,
		provider: string,
		boardsKey: string,
	) => {
		const selectHotel = selectedHotels[getDestinationIndex(destinationCityCode, destinationIndex)];
		return selectHotel
			? selectHotel.hotel.hotelId === hotelId &&
					selectHotel.hotel.provider === provider &&
					selectHotel.hotel.boardsKey == boardsKey
			: false;
	};

	const getBoardsForDestination = (destinationIndex: number, destinationCityCode: string): string => {
		const selectedHotel = selectedHotels[getDestinationIndex(destinationCityCode, destinationIndex)];
		return selectedHotel
			? Array.from(new Set(selectedHotel.hotel.rooms.map((x) => getBoardName(x.board)))).join(',')
			: '';
	};

	const existRepeatDestination = (): boolean =>
		destinations.some(
			(destination, index) => destinations.findIndex((x, i) => x.cityCode == destination.cityCode && i != index) > -1,
		);

	const oneRoomOnQuotation = hasOnlyOneRoom(paxTotals);

	// Qué incluye el precio, para la línea debajo del total. En multi el viaje
	// son varios destinos, así que se agregan los servicios de todos ellos más
	// los manuales. Nunca se enumeran nombres: el helper sólo usa categorías.
	const includedSummary = buildIncludedCategoriesSummary({
		hasFlight: totalFlights > 0,
		hasAccommodation: true,
		serviceNames: [
			...destinations.flatMap((destination) => (destination.services || []).map((service) => service.name)),
			...(manualServices || []).map((service) => service.name),
		],
	});

	// Mensaje de avanzar de whatsapp
	const { auth } = useSelector((state: RootState) => state);
	const countryWAP = countryWAPMapping[auth.country] || '59897683318';

	// Recién acá, con todos los hooks ya ejecutados, se puede cortar el render.
	if (hasInvalidParams) {
		return <> </>;
	}
	const closeDrawer = () => {
		setOpenDrawer(false);
		setOpenOptions(false);
	};

	// Qué hay realmente para elegir: define el copy del control (no prometemos
	// "otro vuelo" si hay un solo itinerario).
	const hasHotelAlternatives = destinations.some((destination) => destination.hotelOptions.length > 1);
	const changeOptionsCopy = buildChangeOptionsCopy({
		hasFlightAlternatives: totalFlights > 1,
		hasHotelAlternatives,
		isOpen: openOptions,
	});
	// Ordenado por destino: al cambiar un hotel, handleSelectHotel manda el
	// elegido al final del array, y sin esto los hoteles quedaban listados en un
	// orden distinto al del itinerario (el hotel del 2º destino primero).
	const selectedHotelsInOrder = [...selectedHotels].sort((a, b) => a.destinationIndex - b.destinationIndex);
	const currentSelectionLabel = buildCurrentSelectionLabel(
		selectedHotelsInOrder.map((selected) => selected.hotel.hotelName),
	);

	const flightMessage =
		flightIndexSelected !== null ? `* Itinerario opción ${flightIndexSelected + 1}` : 'Itinerario no seleccionado';
	const hotelsMessage = selectedHotels
		.map((hotel, index) => {
			const destination = destinations[index];
			return `* ${destination.nights} noches en ${getCityNameCleaned(destination.cityName)} con el hotel ${
				hotel.hotel.hotelName
			}.`;
		})
		.join('\n');

	const additionalMessage =
		selectedOptionalsTotalUSD > 0 || selectedAssistanceTotalUSD > 0
			? `\nAdicionales seleccionados:\n${
					selectedOptionalsTotalUSD > 0 ? `* Opcionales agregados: USD ${selectedOptionalsTotalUSD}.\n` : ''
			  }${
					selectedAssistanceTotalUSD > 0
						? `* Asistencia ${selectedAssistanceName || ''}: USD ${selectedAssistanceTotalUSD}.`
						: ''
			  }`
			: '';
	const optionMessage = `Hola, quiero avanzar con la cotización n* ${quotationId}:\n${flightMessage}.\n${hotelsMessage}${additionalMessage}`;
	const encodeOptionMessage = encodeURIComponent(optionMessage);
	const moveForwardLink = `https://wa.me/${countryWAP}?text=${encodeOptionMessage}`;
	const isColombia = auth.country === 'CO';
	const localAveragePrice = selectedCombination
		? Math.round(selectedCombination.summaryPriceForOneAdult * exchangeRate)
		: null;
	const primaryAveragePrice = selectedCombination
		? isColombia
			? `${localCurrency} ${separadorDeMiles(localAveragePrice || 0)}`
			: `USD ${separadorDeMiles(selectedCombination.summaryPriceForOneAdult)}`
		: '';
	const totalSign =
		selectedCombination?.roomsSummaryPrice.reduce((sum, room) => sum + (room.sign?.totalSign || 0), 0) || 0;
	const totalPackagePrice = selectedCombination?.roomsSummaryPrice.reduce((sum, room) => sum + room.totalPrice, 0) || 0;
	const additionalTotalUSD = selectedOptionalsTotalUSD + selectedAssistanceTotalUSD;
	const updatedTotalPackagePrice = totalPackagePrice + additionalTotalUSD;
	const localTotalPackagePrice = Math.round(totalPackagePrice * exchangeRate);
	const localUpdatedTotalPackagePrice = Math.round(updatedTotalPackagePrice * exchangeRate);
	const localSelectedOptionalsTotal = Math.round(selectedOptionalsTotalUSD * exchangeRate);
	const localSelectedAssistanceTotal = Math.round(selectedAssistanceTotalUSD * exchangeRate);
	const localTotalSign = Math.round(totalSign * exchangeRate);
	const primarySignPrice = isColombia
		? `${localCurrency} ${separadorDeMiles(localTotalSign)}`
		: `USD ${separadorDeMiles(totalSign)}`;
	const alternativeSignPrice = isColombia
		? `USD ${separadorDeMiles(totalSign)}`
		: `${localCurrency} ${separadorDeMiles(localTotalSign)}`;
	const primaryTotalPackagePrice = isColombia
		? `${localCurrency} ${separadorDeMiles(localTotalPackagePrice)}`
		: `USD ${separadorDeMiles(totalPackagePrice)}`;
	// Margen bruto de la selección (sólo agente): suma habitación por habitación
	// para que coincida exactamente con los "mb:" de cada card del detalle.
	const selectionGrossMargin =
		selectedCombination != null
			? selectedCombination.roomsSummaryPrice.reduce(
					(total, room) => total + calculateUtility(room.totalPrice, markup),
					0,
			  )
			: 0;
	const primaryUpdatedTotalPackagePrice = isColombia
		? `${localCurrency} ${separadorDeMiles(localUpdatedTotalPackagePrice)}`
		: `USD ${separadorDeMiles(updatedTotalPackagePrice)}`;
	const alternativeUpdatedTotalPackagePrice = isColombia
		? `USD ${separadorDeMiles(updatedTotalPackagePrice)}`
		: `${localCurrency} ${separadorDeMiles(localUpdatedTotalPackagePrice)}`;
	const primaryOptionalsTotal = isColombia
		? `${localCurrency} ${separadorDeMiles(localSelectedOptionalsTotal)}`
		: `USD ${separadorDeMiles(selectedOptionalsTotalUSD)}`;
	const primaryAssistanceTotal = isColombia
		? `${localCurrency} ${separadorDeMiles(localSelectedAssistanceTotal)}`
		: `USD ${separadorDeMiles(selectedAssistanceTotalUSD)}`;

	return (
		<Container sx={{ marginBottom: `${bottomBarSpace}px`, paddingX: 0 }}>
			{destinations.length &&
				destinations.map((destination, destinationIndex) => (
					<React.Fragment key={`destination-hotels-${destinationIndex}-${destination.cityCode}`}>
						<Typography component="h1" variant="h6" sx={sectionHeaderSx}>
							Alojamiento en: {getCityNameCleaned(destination.cityName)}
						</Typography>
						<Typography
							variant="body2"
							sx={{
								px: 1,
								pb: 1,
								color: 'text.secondary',
								fontWeight: 500,
								borderLeft: '3px solid',
								borderColor: 'secondary.main',
								ml: 0.5,
								mb: 1,
							}}
						>
							{destination.hotelOptions.length} opciones disponibles. Tocá una tarjeta para cambiar tu selección.
						</Typography>
						<Container
							disableGutters
							component="main"
							maxWidth="md"
							key={destination.cityCode}
							sx={{ paddingLeft: '0', paddingRight: '0', paddingBottom: 1 }}
						>
							{/* Mismo criterio que las habitaciones (CardsCarousel): mobile
							    carrusel con flechas y contador, desktop grilla envolvente
							    para comparar todas las opciones de un vistazo. */}
							<CardsCarousel itemLabel="Hotel" wrapOnDesktop itemWidth={{ xs: '88%', sm: 340, md: 350 }}>
								{destination.hotelOptions.map((hotel) => (
									<HotelDetailCardMultiDestination
										key={`hotel-card-${destination.cityCode}-${destinationIndex}-${hotel.hotelId}-${hotel.provider}-${hotel.boardsKey}`}
										isSelected={isSelectedHotel(
											hotel.hotelId,
											destination.cityCode,
											destinationIndex,
											hotel.provider,
											hotel.boardsKey,
										)}
										handleSelectHotel={() => handleSelectHotel(destination.cityCode, destinationIndex, hotel)}
										hotelOption={hotel}
										isExternal={isExternal}
										paxTotals={paxTotals}
									/>
								))}
							</CardsCarousel>
						</Container>
					</React.Fragment>
				))}
			{/* Barra fija propia (mismo patrón que destino simple, que se porta bien
			    en iPhone): pegada al viewport, sin depender de cálculos de alto. */}
			<Box
				sx={{
					position: 'fixed',
					bottom: 0,
					left: 0,
					right: 0,
					zIndex: (theme) => theme.zIndex.drawer - 1,
					backgroundColor: '#fff',
					borderTopLeftRadius: 14,
					borderTopRightRadius: 14,
					boxShadow: '0 -10px 30px rgba(49, 25, 75, 0.16)',
					paddingBottom: 'env(safe-area-inset-bottom)',
				}}
			>
				<Grid
					p={{ xs: 2, sm: 1.25 }}
					pb={{ xs: 3, sm: 1.25 }}
					paddingTop={{ xs: 1, sm: 1.25 }}
					justifyContent="space-between"
					alignItems="center"
					container
				>
					<Grid xs={12} sm="auto" item>
						<Typography
							component="h1"
							variant="h6"
							sx={{
								marginBottom: { xs: '4px', sm: 0 },
								width: { xs: '100%', sm: 'auto' },
								background: branding.headerGradient,
								paddingLeft: '16px',
								paddingRight: '16px',
								paddingTop: { xs: '8px', sm: '7px' },
								paddingBottom: { xs: '8px', sm: '7px' },
								borderRadius: '10px',
								fontSize: { xs: '1rem', sm: '0.9rem' },
								fontWeight: 700,
								color: 'white',
								boxShadow: SECTION_SHADOW,
								whiteSpace: 'nowrap',
							}}
						>
							Tu selección:{' '}
						</Typography>
					</Grid>
					<Container
						maxWidth="md"
						sx={{
							display: 'flex',
							alignItems: 'center',
							flex: 1,
							gap: 1,
							lineHeight: '1.0',
							fontFamily: 'Montserrat, sans-serif',
							paddingLeft: '8px',
							paddingRight: '8px',
						}}
					>
						<Grid xs item minWidth={0}>
							{selectedCombination && (
								<Box minWidth={0} sx={{ overflow: 'hidden' }}>
									{/* Jerarquía: el número protagonista es SIEMPRE el total del grupo.
										    La seña es una facilidad de pago y va como línea secundaria —
										    si el titular es la seña, el cliente entiende que ese es el
										    precio del viaje. */}
									<Box minWidth={0}>
										<Typography component="p" fontWeight={900} color="primary" fontSize={12} lineHeight={1.1}>
											{additionalTotalUSD > 0 ? 'Total con adicionales' : 'Total del viaje'}
										</Typography>
										<Typography
											component="p"
											fontSize={{ xs: 19, sm: 20 }}
											fontWeight={900}
											color="primary"
											lineHeight={1.05}
											sx={{
												mt: 0.25,
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											}}
										>
											{primaryUpdatedTotalPackagePrice}
										</Typography>
										{/* Margen bruto de lo elegido, SÓLO para el agente (showUtility) y
										    pegado al total: así lo ve moverse mientras cambia de hotel, sin
										    tener que abrir el detalle. En destino simple ya se ve en la página. */}
										{showUtility && markup != null && (
											<Typography
												component="p"
												fontSize={12}
												fontWeight={800}
												lineHeight={1.1}
												sx={{ color: 'info.dark' }}
											>
												mb: {selectionGrossMargin}
											</Typography>
										)}
										{totalSign > 0 && (
											// El MONTO nunca se trunca (flexShrink 0): en pantalla angosta el
											// ancho lo cede la etiqueta, no el número — la seña es el gancho de
											// venta. Acá va la etiqueta corta; la frase completa vive en el drawer.
											<Box display="flex" alignItems="baseline" gap={0.5} mt={0.25} sx={{ minWidth: 0 }}>
												<Typography
													component="span"
													fontSize={12}
													fontWeight={800}
													lineHeight={1.1}
													noWrap
													sx={{ color: SIGN_GREEN, flexShrink: 1, minWidth: 0 }}
												>
													{isColombia ? 'Reserva desde' : 'Seña'}
												</Typography>
												<Typography
													component="span"
													fontSize={12}
													fontWeight={800}
													lineHeight={1.1}
													sx={{ color: SIGN_GREEN, flexShrink: 0, whiteSpace: 'nowrap' }}
												>
													{primarySignPrice}
												</Typography>
											</Box>
										)}
									</Box>
									<Box display="flex" alignItems="center" gap={0.75} mt={0.4} flexWrap="wrap">
										<Typography component="span" fontSize={11} fontWeight={700} color="text.secondary" lineHeight={1.1}>
											Prom. adulto {primaryAveragePrice}
										</Typography>
										<Button
											size="small"
											variant="text"
											onClick={() => setShowAlternativeCurrency((value) => !value)}
											sx={{
												p: 0,
												minWidth: 'auto',
												textTransform: 'none',
												fontSize: 10,
												fontWeight: 900,
												color: 'primary.main',
											}}
										>
											{showAlternativeCurrency ? 'Ocultar' : isColombia ? 'USD' : 'Moneda local'}
										</Button>
									</Box>
									{showAlternativeCurrency && (
										<Typography component="p" fontSize={11} fontWeight={700} color="text.secondary" lineHeight={1.1}>
											Total {alternativeUpdatedTotalPackagePrice}
											{totalSign > 0 && ` · Seña ${alternativeSignPrice}`}
										</Typography>
									)}
								</Box>
							)}
						</Grid>

						<Grid
							item
							textAlign="center"
							justifyContent="center"
							sx={{ pointerEvents: 'all', display: 'flex', alignItems: 'center', gap: 0.75 }}
						>
							<Button
								variant="contained"
								color="secondary"
								href={moveForwardLink}
								rel="noopener noreferrer"
								target="_blank"
								startIcon={<WhatsAppIcon />}
								title="Le llega a tu asesor por WhatsApp. No es un pago."
								sx={{
									borderRadius: '999px',
									textTransform: 'none',
									fontWeight: 900,
									fontSize: 13,
									px: { xs: 1.1, sm: 1.6 },
									py: 0.9,
									minWidth: { xs: 78, sm: 92 },
									boxShadow: '0 8px 18px rgba(237, 102, 2, 0.22)',
									whiteSpace: 'nowrap',
								}}
							>
								Reservar
							</Button>
							<IconButton
								sx={{
									backgroundColor: 'secondary.main',
									color: 'white',
									width: 40,
									height: 40,
									...(!openDrawer && {
										boxShadow: '0 8px 18px rgba(237, 102, 2, 0.24)',
										animation: 'selectionCtaPulse 1.8s ease-in-out infinite',
										// Quien pidió menos animación (o tiene un equipo flojo con esa
										// preferencia activa) no necesita un latido infinito.
										'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
										'@keyframes selectionCtaPulse': {
											'0%': { transform: 'scale(1)', opacity: 1 },
											'50%': { transform: 'scale(1.06)', opacity: 0.92 },
											'100%': { transform: 'scale(1)', opacity: 1 },
										},
									}),
									'&:hover': {
										'@media (hover: none)': {
											backgroundColor: 'secondary.main',
											'&:active': {
												backgroundColor: 'secondary.main',
											},
										},
									},
									'&:active': {
										backgroundColor: 'secondary.main',
									},
								}}
								onClick={() => (openDrawer ? closeDrawer() : setOpenDrawer(true))}
								aria-label={openDrawer ? 'Cerrar detalles' : 'Ver detalles'}
							>
								{openDrawer ? <ExpandMoreOutlined /> : <AddOutlined />}
							</IconButton>
						</Grid>
					</Container>
				</Grid>
			</Box>
			{/* El detalle expandido: Drawer común (no Swipeable). El swipeable
			    posicionaba la barra con un translate calculado sobre un alto en %
			    del viewport, y en iPhone el viewport cambia de alto al colapsarse la
			    barra de Safari: el cálculo quedaba viejo y la barra aterrizaba a
			    media pantalla con el contenido pasando por debajo. */}
			<Drawer
				anchor="bottom"
				open={openDrawer}
				onClose={closeDrawer}
				ModalProps={{ keepMounted: true }}
				PaperProps={{
					sx: {
						maxHeight: '82vh',
						borderTopLeftRadius: 14,
						borderTopRightRadius: 14,
						backgroundColor: '#fbfafc',
						paddingBottom: 'env(safe-area-inset-bottom)',
					},
				}}
			>
				{openDrawer && (
					<Paper
						sx={{
							height: '100%',
							overflowY: 'auto',
							backgroundColor: '#fbfafc',
							WebkitOverflowScrolling: 'touch',
							overscrollBehavior: 'contain',
						}}
					>
						<Container sx={{ pt: 1.5 }}>
							{/* Salida explícita: tocar afuera también cierra, pero este público
							    necesita la cruz. */}
							<Box display="flex" justifyContent="flex-end" mb={0.5}>
								<IconButton
									onClick={closeDrawer}
									aria-label="Cerrar detalle"
									size="small"
									sx={{ backgroundColor: 'rgba(49, 25, 75, 0.06)', color: 'primary.main' }}
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							</Box>
							<Accordion
								disableGutters
								elevation={0}
								expanded={openOptions}
								onChange={(_, expanded) => setOpenOptions(expanded)}
								sx={{
									mb: 1.25,
									borderRadius: '12px',
									border: '1px solid rgba(49, 25, 75, 0.10)',
									boxShadow: '0 6px 18px rgba(49, 25, 75, 0.08)',
									overflow: 'hidden',
									'&:before': { display: 'none' },
								}}
							>
								{/* Sin expandIcon de MUI: la affordance es la pastilla con texto +
								    chevron. MUI rota el expandIcon al abrir, y rotar una pastilla con
								    texto adentro queda ilegible. */}
								<AccordionSummary
									sx={{
										minHeight: 48,
										backgroundColor: 'rgba(49, 25, 75, 0.035)',
										'& .MuiAccordionSummary-content': { my: 1, minWidth: 0 },
									}}
								>
									<Box display="flex" alignItems="center" gap={1} width="100%">
										<Box flexGrow={1} minWidth={0}>
											<Typography variant="body2" color="primary" fontWeight={900}>
												{changeOptionsCopy.title}
											</Typography>
											{currentSelectionLabel && (
												<Typography
													variant="caption"
													color="text.secondary"
													fontWeight={600}
													sx={{
														display: '-webkit-box',
														WebkitLineClamp: 2,
														WebkitBoxOrient: 'vertical',
														overflow: 'hidden',
													}}
												>
													{currentSelectionLabel}
												</Typography>
											)}
										</Box>
										{/* Pastilla, no botón: el AccordionSummary ya ES el botón y anidar
										    otro control adentro sería HTML inválido. */}
										<Box
											display="flex"
											alignItems="center"
											gap={0.25}
											flexShrink={0}
											sx={{
												border: '1px solid rgba(49, 25, 75, 0.28)',
												borderRadius: '999px',
												backgroundColor: '#fff',
												color: 'primary.main',
												py: 0.4,
												pl: 1.25,
												pr: 0.75,
											}}
										>
											<Typography fontSize={12} fontWeight={800} lineHeight={1.2}>
												{changeOptionsCopy.action}
											</Typography>
											<ExpandMoreOutlined
												fontSize="small"
												sx={{
													transform: openOptions ? 'rotate(180deg)' : 'none',
													transition: 'transform 150ms ease',
												}}
											/>
										</Box>
									</Box>
								</AccordionSummary>
								<AccordionDetails sx={{ p: 1.25, pt: 0, backgroundColor: '#fbfafc' }}>
									{selectedCombination && flightIndexSelected != null && totalFlights > 1 && (
										<>
											<Typography component="h1" variant="h6" sx={drawerSectionTitleSx}>
												Vuelo seleccionado
											</Typography>
											<Container disableGutters sx={drawerScrollSx}>
												<Grid spacing={2} py={1} wrap="nowrap" justifyContent={{ sx: 'right' }} container>
													{Array.from({ length: totalFlights }, (_, index) => index).map((fIndex) => {
														const isSelected = fIndex === flightIndexSelected;
														return (
															<FlightOption
																key={`flight-${fIndex}`}
																isSelected={isSelected}
																flightIndex={fIndex}
																handleFlightSelectionChange={handleFlightSelectionChange}
															/>
														);
													})}
												</Grid>
											</Container>
										</>
									)}
									{destinations.length &&
										destinations.map((destination, destinationIndex) => (
											<React.Fragment key={`drawer-destination-${destinationIndex}-${destination.cityCode}`}>
												<Typography component="h1" variant="h6" sx={drawerSectionTitleSx}>
													{destination.nights} noches en {getCityNameCleaned(destination.cityName)}
												</Typography>
												{existRepeatDestination() &&
													getBoardsForDestination(destinationIndex, destination.cityCode) != '' && (
														<Typography textAlign={{ sx: 'right', md: 'center' }} fontWeight="bold" color="primary">
															{getBoardsForDestination(destinationIndex, destination.cityCode)}{' '}
														</Typography>
													)}
												<Container disableGutters sx={drawerScrollSx} key={`${destination.cityCode}-container-rooms`}>
													<Grid spacing={2} py={1} wrap="nowrap" justifyContent={{ sx: 'right' }} container>
														{destination.hotelOptions.length > 0 &&
															destination.hotelOptions.map((hotel) => (
																<HotelOptionItem
																	key={`${hotel.hotelId}-${hotel.provider}-${hotel.boardsKey}`}
																	isSelected={isSelectedHotel(
																		hotel.hotelId,
																		destination.cityCode,
																		destinationIndex,
																		hotel.provider,
																		hotel.boardsKey,
																	)}
																	disabled={
																		!isHotelAvailableForCurrentSelection(destination.cityCode, destinationIndex, hotel)
																	}
																	hotel={hotel}
																	onSelect={() => handleSelectHotel(destination.cityCode, destinationIndex, hotel)}
																/>
															))}
													</Grid>
												</Container>
											</React.Fragment>
										))}
								</AccordionDetails>
							</Accordion>
							<Typography
								component="h1"
								variant="h6"
								sx={{
									marginTop: '16px',
									marginBottom: '10px',
									width: '100%',
									background: branding.headerGradient,
									paddingLeft: '16px',
									paddingRight: '16px',
									paddingTop: '9px',
									paddingBottom: '9px',
									borderRadius: '10px',
									fontSize: '1rem',
									fontWeight: 700,
									color: 'white',
									boxShadow: SECTION_SHADOW,
								}}
							>
								Presupuesto:
							</Typography>
							{selectedCombination && (
								<Box
									sx={{
										p: 1.5,
										mb: 1,
										borderRadius: '12px',
										backgroundColor: '#fff',
										border: '1px solid rgba(49, 25, 75, 0.10)',
										boxShadow: '0 6px 18px rgba(49, 25, 75, 0.08)',
									}}
								>
									<Typography variant="caption" color="text.secondary" fontWeight={700}>
										{additionalTotalUSD > 0 ? 'Total actualizado para el grupo' : 'Total del viaje para el grupo'}
									</Typography>
									<Box display="flex" alignItems="baseline" justifyContent="space-between" gap={1} mt={0.25}>
										<Typography variant="h6" color="primary" fontWeight={900} lineHeight={1.1}>
											{primaryUpdatedTotalPackagePrice}
										</Typography>
										{showAlternativeCurrency && (
											<Typography variant="caption" color="text.secondary" fontWeight={800} textAlign="right">
												{alternativeUpdatedTotalPackagePrice}
											</Typography>
										)}
									</Box>
									{additionalTotalUSD > 0 && (
										<Box mt={1} display="grid" gap={0.5}>
											<Box display="flex" justifyContent="space-between" gap={1}>
												<Typography variant="caption" color="text.secondary" fontWeight={700}>
													Paquete base
												</Typography>
												<Typography variant="caption" color="text.secondary" fontWeight={800}>
													{primaryTotalPackagePrice}
												</Typography>
											</Box>
											{selectedOptionalsTotalUSD > 0 && (
												<Box display="flex" justifyContent="space-between" gap={1}>
													<Typography variant="caption" color="text.secondary" fontWeight={700}>
														Opcionales agregados
													</Typography>
													<Typography variant="caption" color="secondary" fontWeight={900}>
														+ {primaryOptionalsTotal}
													</Typography>
												</Box>
											)}
											{selectedAssistanceTotalUSD > 0 && (
												<Box display="flex" justifyContent="space-between" gap={1}>
													<Typography variant="caption" color="text.secondary" fontWeight={700}>
														Asistencia {selectedAssistanceName}
													</Typography>
													<Typography variant="caption" color="secondary" fontWeight={900}>
														+ {primaryAssistanceTotal}
													</Typography>
												</Box>
											)}
										</Box>
									)}
									{totalSign > 0 && (
										<Box
											display="flex"
											alignItems="center"
											justifyContent="space-between"
											gap={1}
											mt={1}
											p={1}
											borderRadius="10px"
											bgcolor={SIGN_GREEN_BG}
											border={`1px solid ${SIGN_GREEN_BORDER}`}
										>
											<Box display="flex" alignItems="center" gap={0.75}>
												<PaymentsIcon sx={{ fontSize: 20, color: SIGN_GREEN }} />
												<Box>
													<Typography variant="body2" fontWeight={900} lineHeight={1.15} sx={{ color: SIGN_GREEN }}>
														Reservá ahora con seña
													</Typography>
													<Typography variant="caption" color="text.secondary">
														La seña base puede variar al confirmar adicionales.
													</Typography>
												</Box>
											</Box>
											<Box textAlign="right">
												<Typography variant="body2" fontWeight={900} sx={{ color: SIGN_GREEN }}>
													{primarySignPrice}
												</Typography>
												{showAlternativeCurrency && (
													<Typography variant="caption" color="text.secondary" fontWeight={700}>
														{alternativeSignPrice}
													</Typography>
												)}
											</Box>
										</Box>
									)}
									<PaymentTeaser
										isExternal={isExternal}
										totalUSD={updatedTotalPackagePrice}
										formatPrice={(valueUSD) =>
											isColombia
												? `${localCurrency} ${separadorDeMiles(Math.round(valueUSD * exchangeRate))}`
												: `USD ${separadorDeMiles(valueUSD)}`
										}
									/>
								</Box>
							)}
							{/* El scroll horizontal (si hace falta) lo maneja RoomCardsCarousel
							    adentro de la lista, con flechas y contador. */}
							<Container disableGutters sx={{ marginY: oneRoomOnQuotation ? 0 : 2 }}>
								{selectedHotels.length > 0 && selectedCombination && (
									<RoomPricesMultiDestinationPreviewList
										hotels={selectedHotelsInOrder.map((selected) => selected.hotel)}
										combination={selectedCombination}
										localCurrency={localCurrency}
										exchangeRate={exchangeRate}
										showLocalCurrency={showAlternativeCurrency && showLocalCurrency}
										primaryInLocalCurrency={isColombia}
										paxTotals={paxTotals}
										// En mobile también: en destino simple ya se veía y esconderlo por
										// ancho de pantalla era arbitrario para un usuario interno.
										showUtility={showUtility}
										markup={markup}
										includedSummary={includedSummary}
										isExternal={isExternal}
									/>
								)}
							</Container>
							{/* Margen bruto TOTAL de la selección (suma de las habitaciones), sólo
							    para el agente. Igual que en destino simple: se suma habitación por
							    habitación para que coincida con los "mb:" de cada card. */}
							{showUtility && markup != null && selectedCombination && (
								<Card
									sx={{
										marginBottom: '8px',
										padding: '10px 14px',
										borderRadius: '12px',
										border: '1px solid rgba(49, 25, 75, 0.10)',
										display: 'flex',
										alignItems: 'baseline',
										justifyContent: 'space-between',
										gap: 1,
									}}
								>
									<Typography variant="body2" fontWeight={800} color="primary">
										Margen bruto de la selección
									</Typography>
									<Typography variant="body2" fontWeight={900} sx={{ color: 'info.dark' }}>
										mb:{' '}
										{selectedCombination.roomsSummaryPrice.reduce(
											(total, room) => total + calculateUtility(room.totalPrice, markup),
											0,
										)}
									</Typography>
								</Card>
							)}
							<Typography
								component="h1"
								variant="h6"
								sx={{
									width: '100%',
									background: branding.headerGradient,
									paddingLeft: '16px',
									paddingRight: '16px',
									paddingTop: '9px',
									paddingBottom: '9px',
									borderRadius: '10px',
									fontSize: '1rem',
									fontWeight: 700,
									color: 'white',
									boxShadow: SECTION_SHADOW,
								}}
							>
								Incluye:
							</Typography>
							<IncludeMultiDestination
								destinations={destinations}
								manualServices={manualServices}
								hasFlight={totalFlights > 0}
							/>
							<Box sx={{ pb: 3 }}>
								<ReserveCta href={moveForwardLink} />
							</Box>
						</Container>
					</Paper>
				)}
			</Drawer>
		</Container>
	);
};
